import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"]

  /** @type {number | undefined} */
  #animationTimeout

  /** @type {number | undefined} */
  #cleanupTimeout

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener("pointerenter", this.#preloadImage)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)
    this.removeEventListener("pointerenter", this.#preloadImage)
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    //this.animateAddToCart()

    // Disable fly-to-cart on homepage and product pages
    const tpl = document.querySelector('main')?.dataset?.template || ''
    const isHome = tpl.includes('index')
    const isProduct = tpl.includes('product')

    if (!event.target.closest(".quick-add-modal") && !(isHome || isProduct)) {
      this.#animateFlyToCart()
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia

    if (!image) return

    preloadImage(image)
  }

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs
    const cartIcon = document.querySelector(".header-actions__cart-icon")

    const image = this.dataset.productVariantMedia

    if (!cartIcon || !addToCartButton || !image) return

    const flyToCartElement = document.createElement("div")
    flyToCartElement.className = "fly-to-cart-animation"
    flyToCartElement.style.backgroundImage = `url(${image})`

    // Get positions
    const buttonRect = addToCartButton.getBoundingClientRect()
    const cartRect = cartIcon.getBoundingClientRect()

    // Set initial position
    flyToCartElement.style.left = `${buttonRect.left + buttonRect.width / 2 - 25}px`
    flyToCartElement.style.top = `${buttonRect.top + buttonRect.height / 2 - 25}px`

    document.body.appendChild(flyToCartElement)

    // Animate to cart
    requestAnimationFrame(() => {
      flyToCartElement.style.transition = "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
      flyToCartElement.style.left = `${cartRect.left + cartRect.width / 2 - 25}px`
      flyToCartElement.style.top = `${cartRect.top + cartRect.height / 2 - 25}px`
      flyToCartElement.style.transform = "scale(0.3)"
      flyToCartElement.style.opacity = "0.7"
    })

    // Remove element after animation
    setTimeout(() => {
      if (flyToCartElement.parentNode) {
        flyToCartElement.parentNode.removeChild(flyToCartElement)
      }
    }, 800)
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added")
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added")
      }, 10)
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION)
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent)
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.#initSeatbeltDropdowns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (!this.#validateSeatbeltProperties(form)) {
      return;
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.add(item.dataset.sectionId);
      }
    });
    formData.append('sections', Array.from(cartItemComponentsSectionIds).join(','));
    formData.append('sections_url', window.location.pathname);

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }
          // const allowed = ['cart-drawer', 'cart-items', 'cart-icon-bubble', 'cart-notification'];
          // const filteredSections = {};
          // for (const key in (response.sections || {})) {
          //   if (allowed.includes(key)) {
          //     filteredSections[key] = response.sections[key];
          //   }
          // }
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // Ensure cart drawer opens after a successful add-to-cart
          try {
            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          } catch (e) {
            // no-op: if drawer is not present (e.g., cart type = page)
          }
          // Signal to downstream listeners that the cart drawer has updated
          // Give the theme a tick to finish morphing sections first
          setTimeout(() => {
            try { document.dispatchEvent(new Event('cart:rendered')); } catch(_) {}
          }, 0);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Validates any seat belt property fields before submitting the form.
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #validateSeatbeltProperties(form) {
    const propertyInputs = form.querySelectorAll('.extra__details-input');

    if (!propertyInputs.length) return true;

    let isValid = true;
    const errorMessage = form.querySelector('[data-seatbelt-error]');

    propertyInputs.forEach((input) => {
      const wrapper = input.closest('[data-seatbelt-wrapper]');
      if (wrapper) {
        wrapper.classList.remove('has-error');
      } else {
        input.style.borderColor = '';
      }
      const isRequired = input.hasAttribute('required') && input.getAttribute('required') !== 'false';
      if (isRequired && !input.value.trim()) {
        isValid = false;
        if (wrapper) {
          wrapper.classList.add('has-error');
        } else {
          input.style.borderColor = 'red';
        }
      }
    });

    if (errorMessage) {
      errorMessage.hidden = isValid;
      errorMessage.style.color = isValid ? '#ADADAD' : 'red';
    }

    return isValid;
  }

  #initSeatbeltDropdowns() {
    const wrappers = this.querySelectorAll('[data-seatbelt-wrapper]');
    if (!wrappers.length) return;

    const signal = this.#abortController.signal;

    wrappers.forEach((wrapper) => {
      const dropdown = wrapper.querySelector('[data-seatbelt-dropdown]');
      const trigger = dropdown?.querySelector('[data-seatbelt-trigger]');
      const options = dropdown?.querySelectorAll('[data-seatbelt-option]');
      const nativeSelect = wrapper.querySelector('[data-seatbelt-native]');

      if (!dropdown || !trigger || !options?.length || !nativeSelect) return;

      wrapper.dataset.seatbeltReady = 'true';

      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.#toggleSeatbeltDropdown(dropdown);
        },
        { signal }
      );

      nativeSelect.addEventListener(
        'change',
        () => {
          this.#syncSeatbeltDropdownFromSelect(wrapper, nativeSelect);
        },
        { signal }
      );

      options.forEach((option) => {
        option.addEventListener(
          'click',
          () => {
            const value = option.getAttribute('data-value') || option.textContent?.trim() || '';
            if (!value) return;
            nativeSelect.value = value;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            this.#closeSeatbeltDropdown(dropdown);
          },
          { signal }
        );
      });

      this.#syncSeatbeltDropdownFromSelect(wrapper, nativeSelect);
    });

    document.addEventListener('click', this.#handleSeatbeltOutsideClick, { signal });
  }

  #querySeatbeltDropdowns() {
    return Array.from(this.querySelectorAll('[data-seatbelt-dropdown]'));
  }

  #toggleSeatbeltDropdown(dropdown) {
    const isOpen = dropdown.classList.contains('is-open');
    this.#closeAllSeatbeltDropdowns(dropdown);
    dropdown.classList.toggle('is-open', !isOpen);
    dropdown.querySelector('[data-seatbelt-trigger]')?.setAttribute('aria-expanded', (!isOpen).toString());
  }

  #closeSeatbeltDropdown(dropdown) {
    dropdown.classList.remove('is-open');
    dropdown.querySelector('[data-seatbelt-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  #closeAllSeatbeltDropdowns(exclude) {
    this.#querySeatbeltDropdowns().forEach((dropdown) => {
      if (dropdown === exclude) return;
      this.#closeSeatbeltDropdown(dropdown);
    });
  }

  #syncSeatbeltDropdownFromSelect(wrapper, select) {
    const dropdown = wrapper.querySelector('[data-seatbelt-dropdown]');
    const valueNode = dropdown?.querySelector('[data-seatbelt-value]');
    if (!dropdown || !valueNode) return;

    const value = select.value;
    const indicatorMarkup = dropdown.dataset.seatbeltRequiredIndicator || '';
    if (value) {
      this.#setSeatbeltValueNode(valueNode, value, indicatorMarkup);
      dropdown.classList.add('has-value');
      wrapper.classList.remove('has-error');
    } else {
      const placeholder = dropdown.dataset.seatbeltPlaceholder || '';
      this.#setSeatbeltValueNode(valueNode, placeholder, indicatorMarkup);
      dropdown.classList.remove('has-value');
    }
  }

  #handleSeatbeltOutsideClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      this.#closeAllSeatbeltDropdowns();
      return;
    }

    if (!this.contains(target)) {
      this.#closeAllSeatbeltDropdowns();
      return;
    }

    const dropdown = target.closest('[data-seatbelt-dropdown]');
    if (!dropdown) {
      this.#closeAllSeatbeltDropdowns();
    } else {
      this.#closeAllSeatbeltDropdowns(dropdown);
    }
  };

  #setSeatbeltValueNode(node, text, indicatorMarkup) {
    node.textContent = text;
    if (indicatorMarkup) {
      node.insertAdjacentHTML('beforeend', indicatorMarkup);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
